import { useCallback, useRef, useState } from 'react';

import {
  showActionToast,
  showErrorToast,
  showSuccessToast,
} from '@/features/agent/job_change_processes/lib/toast';
import useCancelCandidateMail from '../api/useCancelCandidateMail';
import useCreateCandidateMail from '../api/useCreateCandidateMail';
import useCreateCandidateMailTemplate from '../api/useCreateCandidateMailTemplate';
import useFetchCandidateMailTemplates from '../api/useFetchCandidateMailTemplates';
import useFetchCandidateMailSetting from '../api/useFetchCandidateMailSetting';
import type { CandidateRecipient } from '../lib/recipients';
import type { MailVariableEditorHandle } from '../components/MailVariableEditor';
import { SEND_UNDO_WINDOW_MS } from '../constants';
import { isMailSenderConfigured } from '../lib/mailSettings';
import { toMinuteIsoString } from '../lib/scheduledAt';

export type MailDestination = 'sent' | 'scheduled';

type SendConfirmation = { scheduledAt: Date | null };

type UseCandidateMailComposerOptions = {
  recipients: CandidateRecipient[];
  onFinished: (destination: MailDestination) => void;
};

export default function useCandidateMailComposer({
  recipients,
  onFinished,
}: UseCandidateMailComposerOptions) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [sendConfirmation, setSendConfirmation] = useState<SendConfirmation | null>(null);
  const [pendingTemplateId, setPendingTemplateId] = useState<number | null>(null);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateSaveDestination, setTemplateSaveDestination] = useState<MailDestination | null>(
    null
  );
  const bodyEditorRef = useRef<MailVariableEditorHandle>(null);
  // isSending(React Query の isPending)は再レンダリングまで反映されないため、同期的にガードする
  const isSendingRef = useRef(false);

  const { data: mailSettingData } = useFetchCandidateMailSetting();
  const {
    data: templates = [],
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useFetchCandidateMailTemplates();
  const { mutateAsync: createCandidateMail, isPending: isSending } = useCreateCandidateMail();
  const { mutateAsync: cancelCandidateMail } = useCancelCandidateMail();
  const { mutateAsync: createCandidateMailTemplate, isPending: isSavingTemplate } =
    useCreateCandidateMailTemplate();

  const mailSettings = mailSettingData?.mail_settings ?? null;
  const isSenderConfigured = isMailSenderConfigured(mailSettings);

  const undoSend = useCallback(
    async (mailId: number) => {
      try {
        await cancelCandidateMail(mailId);
        showSuccessToast('送信を取り消しました');
      } catch (error) {
        showErrorToast(
          error instanceof Error && error.message ? error.message : '送信の取り消しに失敗しました。'
        );
      }
    },
    [cancelCandidateMail]
  );

  const applyTemplate = useCallback(
    (templateId: number) => {
      const template = templates.find((item) => item.id === templateId);
      if (!template) return;
      setSubject(template.subject ?? '');
      setBody(template.body ?? '');
    },
    [templates]
  );

  const selectTemplate = (templateId: number) => {
    const hasExistingInput = subject.trim().length > 0 || body.trim().length > 0;
    if (hasExistingInput) {
      setPendingTemplateId(templateId);
      return;
    }
    applyTemplate(templateId);
  };

  const confirmTemplateApply = () => {
    if (pendingTemplateId === null) return;
    applyTemplate(pendingTemplateId);
    setPendingTemplateId(null);
  };

  const templateMenuMessage = isLoadingTemplates
    ? 'テンプレートを読み込み中...'
    : isTemplatesError
      ? 'テンプレートを取得できません'
      : templates.length === 0
        ? '利用可能なテンプレートがありません'
        : null;

  const submitSchedule = (date: Date) => {
    setIsScheduleDialogOpen(false);
    setSendConfirmation({ scheduledAt: date });
  };

  const send = async () => {
    if (isSendingRef.current || !sendConfirmation) return;
    isSendingRef.current = true;
    const { scheduledAt } = sendConfirmation;

    if (recipients.length === 0) {
      showErrorToast('送信先が選択されていません。');
      isSendingRef.current = false;
      return;
    }

    try {
      const mail = await createCandidateMail({
        subject: subject.trim(),
        body,
        candidateProfileIds: recipients.map((recipient) => recipient.id),
        scheduledAt: scheduledAt ? toMinuteIsoString(scheduledAt) : undefined,
      });
      if (scheduledAt) {
        showSuccessToast('送信を予約しました');
      } else {
        showActionToast(
          '1分後に送信します',
          '取り消す',
          () => void undoSend(mail.id),
          SEND_UNDO_WINDOW_MS
        );
      }
      setSendConfirmation(null);
      setIsPreviewOpen(false);

      const destination: MailDestination = scheduledAt ? 'scheduled' : 'sent';
      if (saveAsTemplate) {
        setTemplateSaveDestination(destination);
        return;
      }
      onFinished(destination);
    } catch (error) {
      console.error(error);
      showErrorToast(
        error instanceof Error && error.message ? error.message : 'メールの送信に失敗しました。'
      );
    } finally {
      isSendingRef.current = false;
    }
  };

  const finishTemplateSave = () => {
    const destination = templateSaveDestination ?? 'sent';
    setTemplateSaveDestination(null);
    setSaveAsTemplate(false);
    onFinished(destination);
  };

  const saveTemplate = async (name: string) => {
    try {
      await createCandidateMailTemplate({ name, subject: subject.trim(), body });
      showSuccessToast('テンプレートを保存しました');
    } catch (error) {
      console.error(error);
      showErrorToast('テンプレートの保存に失敗しました');
    }
    finishTemplateSave();
  };

  return {
    recipients,
    subject,
    setSubject,
    body,
    setBody,
    bodyEditorRef,
    templates,
    templateMenuMessage,
    selectTemplate,
    pendingTemplateId,
    confirmTemplateApply,
    cancelTemplateApply: () => setPendingTemplateId(null),
    senderName: mailSettings?.sender ?? mailSettingData?.organization_name ?? '',
    senderEmail: mailSettings?.reply_address ?? null,
    signature: mailSettings?.signature ?? null,
    mailRecipientLimit: mailSettingData?.mail_recipient_limit ?? null,
    isSenderConfigured,
    isSenderSettingRequired: mailSettingData !== undefined && !isSenderConfigured,
    isPreviewDisabled:
      subject.trim().length === 0 || body.trim().length === 0 || !isSenderConfigured,
    isPreviewOpen,
    openPreview: () => setIsPreviewOpen(true),
    closePreview: () => setIsPreviewOpen(false),
    isScheduleDialogOpen,
    openScheduleDialog: () => setIsScheduleDialogOpen(true),
    closeScheduleDialog: () => setIsScheduleDialogOpen(false),
    submitSchedule,
    sendConfirmation,
    requestSendNow: () => setSendConfirmation({ scheduledAt: null }),
    closeSendConfirmation: () => setSendConfirmation(null),
    send,
    isSending,
    saveAsTemplate,
    setSaveAsTemplate,
    isTemplateSaveOpen: templateSaveDestination !== null,
    finishTemplateSave,
    saveTemplate,
    isSavingTemplate,
    isFollowUpOpen:
      isPreviewOpen ||
      isScheduleDialogOpen ||
      sendConfirmation !== null ||
      templateSaveDestination !== null,
  };
}

export type CandidateMailComposer = ReturnType<typeof useCandidateMailComposer>;
